import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, XCircle, PackageOpen, Home, Store, Sparkles } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PublicFooter from '../components/layout/PublicFooter';
import MarketplaceBasketCard from '../components/marketplace/MarketplaceBasketCard';
import StrategyFilters from '../components/marketplace/StrategyFilters';
import PublicTopNav from '../components/layout/PublicTopNav';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import LeadCaptureModal from '../components/marketplace/LeadCaptureModal';
import MarketplaceFAQ from '../components/marketplace/MarketplaceFAQ';
import { ZEBU_SIGNUP_URL } from '../config/tenantConfig';
import { usePromotion } from '../hooks/usePromotion';
import { MarketplaceListing, MarketplaceListingDetail, MarketplaceSubscription } from '../types/marketplace';
import marketplaceService from '../services/marketplaceService';

const LEAD_MODAL_AUTO_KEY = 'promo_modal_shown';

const SubscribeDialog = React.lazy(() => import('../components/marketplace/SubscribeDialog'));

interface FilterState {
  category?: string;
  risk_level?: string;
  search?: string;
}

const MarketplacePage: React.FC = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [filters, setFilters] = useState<FilterState>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<MarketplaceSubscription[]>([]);
  const [subscribeListing, setSubscribeListing] = useState<MarketplaceListingDetail | null>(null);

  // Check auth status without requiring AuthProvider
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  useEffect(() => {
    try {
      // Check for stored tokens to determine auth state
      // (page can render outside AuthProvider context)
      const tokens = localStorage.getItem('auth_tokens');
      setIsAuthenticated(!!tokens);
    } catch {
      setIsAuthenticated(false);
    }
  }, []);

  // Lead capture — driven by an admin-configured promotion. Zero modal renders
  // when no active promotion targets this surface for the current tenant.
  const { promotion: pagePromo } = usePromotion('marketplace');
  const { promotion: cardPromo } = usePromotion('marketplace-card');
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadModalSource, setLeadModalSource] = useState<'marketplace' | 'marketplace-card'>('marketplace');
  const [leadModalBasketId, setLeadModalBasketId] = useState<string | undefined>(undefined);
  const openLeadModal = useCallback((basketId?: string) => {
    setLeadModalSource(basketId ? 'marketplace-card' : 'marketplace');
    setLeadModalBasketId(basketId);
    setLeadModalOpen(true);
  }, []);
  useEffect(() => {
    if (!pagePromo || !pagePromo.auto_popup) return;
    const today = new Date().toISOString().slice(0, 10);
    const key = `${LEAD_MODAL_AUTO_KEY}_${pagePromo.promo_id}_${today}`;
    try {
      if (localStorage.getItem(key)) return;
    } catch {
      return;
    }
    const t = setTimeout(() => {
      try { localStorage.setItem(key, '1'); } catch {}
      setLeadModalSource('marketplace');
      setLeadModalBasketId(undefined);
      setLeadModalOpen(true);
    }, 2500);
    return () => clearTimeout(t);
  }, [pagePromo]);

  const activePromoForModal = leadModalSource === 'marketplace-card' ? cardPromo : pagePromo;

  const fetchSubscriptions = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const subs = await marketplaceService.getSubscriptions();
      setSubscriptions(subs);
    } catch {
      // non-critical — ignore
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchListings = async () => {
      try {
        setLoading(true);
        setError(null);
        const apiFilters: { category?: string; risk_level?: string } = {};
        if (filters.category) apiFilters.category = filters.category;
        if (filters.risk_level) apiFilters.risk_level = filters.risk_level;
        const [data] = await Promise.all([
          marketplaceService.getListings(Object.keys(apiFilters).length > 0 ? apiFilters : undefined),
          fetchSubscriptions(),
        ]);
        setListings(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load baskets');
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, [filters.category, filters.risk_level, fetchSubscriptions]);

  const subscribedBasketIds = useMemo(
    () => new Set(subscriptions.filter((s) => s.status !== 'CANCELLED').map((s) => s.marketplace_basket_id)),
    [subscriptions]
  );

  const handleSubscribe = useCallback(async (basketId: string) => {
    // Logged-out visitors can see the Subscribe button but must sign in first —
    // send them to the login page rather than opening the subscribe dialog.
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    try {
      const detail = await marketplaceService.getListing(basketId);
      setSubscribeListing(detail);
    } catch {
      // silently fail — user can click View Details instead
    }
  }, [isAuthenticated, navigate]);

  // Client-side search filtering
  const filtered = useMemo(() => {
    if (!filters.search) return listings;
    const q = filters.search.toLowerCase();
    return listings.filter(
      (l) =>
        l.title.toLowerCase().includes(q) ||
        l.description.toLowerCase().includes(q)
    );
  }, [listings, filters.search]);

  const handleRefresh = () => {
    setFilters((f) => ({ ...f }));
  };

  const content = (
    <div className="space-y-4">
      <StrategyFilters filters={filters} onFilterChange={setFilters} />

      {!loading && !error && filtered.length > 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Showing <span className="font-semibold text-gray-700 dark:text-gray-300">{filtered.length}</span> {filtered.length === 1 ? 'basket' : 'baskets'}
        </p>
      )}

      {loading && <LoadingSpinner message="Loading baskets..." />}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-12">
          <PackageOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Baskets Found</h3>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
          {filtered.map((listing) => (
            <MarketplaceBasketCard
              key={listing.basket_id}
              listing={listing}
              onClick={() => navigate(`/marketplace/${listing.basket_id}`)}
              isAuthenticated={isAuthenticated}
              isSubscribed={subscribedBasketIds.has(listing.basket_id)}
              onSubscribe={() => handleSubscribe(listing.basket_id)}
              onManage={() => navigate('/subscriptions')}
              onRegister={cardPromo ? () => openLeadModal(listing.basket_id) : undefined}
            />
          ))}
        </div>
      )}

      {/* Subscribe dialog */}
      {subscribeListing && (
        <React.Suspense fallback={null}>
          <SubscribeDialog
            listing={subscribeListing}
            onClose={() => setSubscribeListing(null)}
            onSuccess={() => {
              setSubscribeListing(null);
              fetchSubscriptions();
            }}
          />
        </React.Suspense>
      )}
    </div>
  );

  // Authenticated users get StandardLayout + PageHeader
  if (isAuthenticated) {
    return (
      <StandardLayout>
        <PageHeader
          title="Algo Marketplace"
          pageType="management"
          icon={<Store className="w-5 h-5" />}
          breadcrumbs={[
            { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
            { label: 'Marketplace' },
          ]}
          actions={
            <div className="flex flex-wrap gap-2">
              {pagePromo && (
                <Button
                  variant="primary"
                  onClick={() => openLeadModal()}
                  leftIcon={<Sparkles className="h-4 w-4" />}
                  className="w-full sm:w-auto"
                >
                  Register Interest
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleRefresh}
                leftIcon={<RefreshCw className="h-4 w-4" />}
                className="w-full sm:w-auto"
              >
                Refresh
              </Button>
            </div>
          }
        />

        <Card>
          <CardHeader>
            <CardTitle>
              Baskets{!loading && !error ? ` (${filtered.length})` : ''}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <StrategyFilters filters={filters} onFilterChange={setFilters} />
            </div>

            {loading && (
              <div className="px-6 py-8">
                <LoadingSpinner message="Loading baskets..." />
              </div>
            )}

            {error && (
              <div className="px-6 py-4">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="text-red-800 dark:text-red-200">{error}</span>
                  </div>
                </div>
              </div>
            )}

            {!loading && !error && filtered.length === 0 && (
              <div className="text-center py-12">
                <PackageOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Baskets Found</h3>
              </div>
            )}

            {!loading && !error && filtered.length > 0 && (
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                  {filtered.map((listing) => (
                    <MarketplaceBasketCard
                      key={listing.basket_id}
                      listing={listing}
                      onClick={() => navigate(`/marketplace/${listing.basket_id}`)}
                      isAuthenticated={isAuthenticated}
                      isSubscribed={subscribedBasketIds.has(listing.basket_id)}
                      onSubscribe={() => handleSubscribe(listing.basket_id)}
                      onRegister={cardPromo ? () => openLeadModal(listing.basket_id) : undefined}
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* FAQ — how to subscribe, daily routine, pause/unsubscribe behaviour. */}
        <MarketplaceFAQ />

        {subscribeListing && (
          <React.Suspense fallback={null}>
            <SubscribeDialog
              listing={subscribeListing}
              onClose={() => setSubscribeListing(null)}
              onSuccess={() => {
                setSubscribeListing(null);
                fetchSubscriptions();
              }}
            />
          </React.Suspense>
        )}

        {activePromoForModal && (
          <LeadCaptureModal
            open={leadModalOpen}
            onClose={() => setLeadModalOpen(false)}
            promotion={activePromoForModal}
            source={leadModalSource}
            basketId={leadModalBasketId}
          />
        )}
      </StandardLayout>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <PublicTopNav />

      {/* Hero — light theme, matching the landing and Build-Your-Own pages */}
      <section className="relative overflow-hidden bg-gradient-to-b from-zebu-50/70 via-white to-white dark:from-gray-900 dark:via-gray-950 dark:to-gray-950">
        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-zebu-200/30 dark:bg-zebu-900/20 rounded-full blur-3xl -z-0" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight text-gray-900 dark:text-white">Algo Marketplace</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-300 max-w-lg">
                Exchange-approved baskets backed by 5 years of backtest data. Subscribe and automate in minutes.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              {pagePromo && (
                <button
                  type="button"
                  onClick={() => openLeadModal()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-zebu-600 hover:bg-zebu-700 text-white font-semibold text-sm shadow-lg shadow-zebu-600/20 hover:shadow-xl transition-all"
                >
                  <Sparkles className="h-4 w-4" />
                  Register Interest
                </button>
              )}
              {!loading && listings.length > 0 && (
                <div className="rounded-xl bg-white dark:bg-gray-900 ring-1 ring-gray-200 dark:ring-gray-800 px-4 py-2 text-center">
                  <p className="text-2xl font-black text-zebu-600 dark:text-zebu-400">{listings.length}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Baskets</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="bg-gray-50 dark:bg-gray-900 py-8 flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {content}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-zebu-700 to-zebu-600 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Ready to automate your trading?</h2>
          <p className="mt-3 text-zebu-100 max-w-lg mx-auto">
            Create a free account, subscribe to a basket, and let it run on your trading account.
          </p>
          <a
            href={ZEBU_SIGNUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-block px-6 py-3.5 rounded-xl bg-white text-zebu-700 font-semibold shadow-lg hover:bg-zebu-50 transition-all"
          >
            Open Demat Account
          </a>
        </div>
      </section>

      <PublicFooter />

      {activePromoForModal && (
        <LeadCaptureModal
          open={leadModalOpen}
          onClose={() => setLeadModalOpen(false)}
          promotion={activePromoForModal}
          source={leadModalSource}
          basketId={leadModalBasketId}
        />
      )}
    </div>
  );
};

export default MarketplacePage;
